import React, { useState } from 'react';
import type { FC } from 'react';
import { isType, match } from 'variant';
import { RepoType } from '@wix/package-manager';
import type { GenerationData } from '../../../types.js';
import { ChooseAppName } from '../../ChooseAppName.js';
import { ChoosePackageName } from '../../ChoosePackageName.js';
import type { TemplateSource } from '../../../parse-command-options.js';
import { ChooseTemplateFlow } from '../ChooseTemplateFlow.js';
import { RegisteringApp } from '../../RegisteringApp.js';
import { AppRegistered } from '../../AppRegistered.js';
import { PostAppRegistration } from '../../PostAppRegistration.js';
import { ConfigMcp } from '../../mcp/ConfigMcp.js';
import { getMcpResult } from '../../mcp/getMcpResult.js';
import type { DataForAppCreation } from '../useAppCreation.js';
import { useAppCreation } from '../useAppCreation.js';

interface NewAppFlowProps {
  template?: TemplateSource;
  extendAppId?: string;
  targetParentFolder: string;
  repoType: RepoType;
  onSubmit: (data: GenerationData) => void;
}

interface AppCreationQuestionsProps {
  template?: TemplateSource;
  targetParentFolder: string;
  repoType: RepoType;
  onSubmit: (data: DataForAppCreation) => void;
}

export const NewAppFlow: FC<NewAppFlowProps> = ({
  onSubmit,
  template: templateFromOptions,
  targetParentFolder,
  repoType,
  extendAppId,
}) => {
  const createApp = useAppCreation({ extendAppId });

  return (
    <>
      {isType(repoType, RepoType.Monorepo) ? (
        <MonorepoFlow
          onSubmit={createApp.execute}
          template={templateFromOptions}
          targetParentFolder={targetParentFolder}
          repoType={repoType}
        />
      ) : (
        <GeneralFlow
          onSubmit={createApp.execute}
          template={templateFromOptions}
          targetParentFolder={targetParentFolder}
          repoType={repoType}
        />
      )}
      {match(createApp.status, {
        Error: () => null,
        Loading: () => <RegisteringApp />,
        Success: ({ result: appCreationData }) => {
          const {
            appData: { appId, appName },
            packageName,
          } = appCreationData;
          return (
            <>
              <AppRegistered appId={appId} appName={appName} />
              <PostAppRegistration
                appId={appId}
                packageName={packageName}
                onPostRegistrationComplete={(data) => {
                  onSubmit({
                    ...appCreationData,
                    appData: { ...appCreationData.appData, ...data },
                  });
                }}
              />
            </>
          );
        },
        NotRequested: () => null,
      })}
    </>
  );
};

const GeneralFlow: FC<AppCreationQuestionsProps> = ({
  onSubmit,
  template: templateFromOptions,
  targetParentFolder,
  repoType,
}) => {
  const [templateData, setTemplateData] = useState<TemplateSource | undefined>(
    templateFromOptions
  );
  const [appName, setAppName] = useState<string>();
  const [packageName, setPackageName] = useState<string>();

  return (
    <>
      {!templateFromOptions && (
        <ChooseTemplateFlow onTemplateChoose={setTemplateData} />
      )}

      {templateData && <ChooseAppName onSubmit={setAppName} />}

      {appName && templateData && (
        <ChoosePackageName
          appName={appName}
          targetParentFolder={targetParentFolder}
          repoType={repoType}
          onSelected={setPackageName}
        />
      )}

      {appName && templateData && packageName && (
        <ConfigMcp
          onSelected={(ideForMcp) => {
            onSubmit({
              template: templateData,
              appName,
              packageName,
              mcpConfigResult: getMcpResult({
                ideForMcp,
                targetParentFolder,
                packageName,
              }),
            });
          }}
        />
      )}
    </>
  );
};

const MonorepoFlow: FC<AppCreationQuestionsProps> = ({
  onSubmit,
  template: templateFromOptions,
  targetParentFolder,
  repoType,
}) => {
  const [templateData, setTemplateData] = useState<TemplateSource | undefined>(
    templateFromOptions
  );
  const [appName, setAppName] = useState<string>();

  return (
    <>
      {!templateFromOptions && (
        <ChooseTemplateFlow onTemplateChoose={setTemplateData} />
      )}

      {templateData && <ChooseAppName onSubmit={setAppName} />}

      {appName && templateData && (
        <ChoosePackageName
          appName={appName}
          targetParentFolder={targetParentFolder}
          repoType={repoType}
          onSelected={(packageName: string) => {
            onSubmit({
              template: templateData,
              appName,
              packageName,
              mcpConfigResult: getMcpResult({
                ideForMcp: undefined,
                targetParentFolder,
                packageName,
              }),
            });
          }}
        />
      )}
    </>
  );
};
